/**
 * CorpseBloomSkillEffect — Poison tree Expel path fork active.
 *
 * Detonate a poisoned corpse within 5 tiles. All remaining poison DoT on the
 * corpse is released as instant burst damage in a 2.5-tile radius around the
 * corpse's position. Poison is consumed (removed from the corpse record) on
 * detonation. If no valid poisoned corpse exists within range, the skill cannot
 * fire — no cooldown spent.
 *
 * Upgrade hooks:
 *   - Bloom Chain (`corpse_bloom_upgrade_2`): post-burst, applies 2s poison to
 *     non-poisoned enemies in the burst radius; extends existing poison by 2s
 *     on already-poisoned enemies.
 *
 * Cooldown tier: long (2000ms).
 *
 * Pure logic — no Phaser dependencies.
 *
 * @see ADR-064 (attunement shared systems architecture)
 * @see ADR-066 (source-tagged DoT coexistence)
 * @see poison-spec.md § Corpse Bloom (corpse_bloom)
 */

import type { SkillEffect } from '../../SkillEffect.js';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

/** Minimal corpse representation for targeting. */
export interface CorpseRecord {
  readonly corpseId: string;
  readonly position: { x: number; y: number };
}

/** Poison instance data on a corpse — mirrors the live PoisonInstance shape. */
export interface CorpsePoisonInstance {
  readonly intensity: number;
  readonly remainingMs: number;
  readonly stacks: number;
  readonly tickIntervalMs: number;
}

/** Minimal living-enemy representation for burst AoE. */
export interface CorpseBloomTarget {
  readonly entityId: string;
  readonly position: { x: number; y: number };
}

// ---------------------------------------------------------------------------
// Dependency interfaces
// ---------------------------------------------------------------------------

export interface CorpseBloomDeps {
  /** Player position and living-enemy queries for burst AoE. */
  readonly targeting: {
    getPlayerPosition(): { x: number; y: number };
    getEnemiesInRange(
      center: { x: number; y: number },
      rangeTiles: number,
    ): ReadonlyArray<CorpseBloomTarget>;
  };
  /** Corpse queries — the integration layer preserves status effect data on
   *  recently-dead enemies for skills like this one. */
  readonly corpses: {
    getPoisonedCorpsesInRange(
      center: { x: number; y: number },
      rangeTiles: number,
    ): ReadonlyArray<CorpseRecord>;
    getPoisonInstances(corpseId: string): ReadonlyArray<CorpsePoisonInstance>;
    removePoisonFromCorpse(corpseId: string): void;
  };
  /** Status effects on living enemies (for Bloom Chain interactions). */
  readonly statusEffects: {
    hasPoisonStatus(entityId: string): boolean;
    applyPoison(
      entityId: string,
      durationMs: number,
      intensity: number,
      sourceSkillId: string,
    ): void;
    extendPoison(entityId: string, extensionMs: number): void;
  };
  /** Damage application. */
  readonly damage: {
    applyBurst(
      entityId: string,
      amount: number,
      sourceSkillId: string,
    ): void;
  };
  /** Resolver for upgrade queries. */
  readonly resolver: {
    getAdditiveEffect(skillId: string, key: string): number;
  };
}

// ---------------------------------------------------------------------------
// Config
// ---------------------------------------------------------------------------

export interface CorpseBloomConfig {
  /** Cooldown in ms (long tier = 2000). */
  readonly cooldownMs: number;
  /** Max range for corpse targeting in tiles. */
  readonly targetRangeTiles: number;
  /** Burst AoE radius in tiles around the detonated corpse. */
  readonly burstRadiusTiles: number;
}

const DEFAULT_CONFIG: CorpseBloomConfig = {
  cooldownMs: 2000,           // long tier
  targetRangeTiles: 5,
  burstRadiusTiles: 2.5,
};

// ---------------------------------------------------------------------------
// Skill effect
// ---------------------------------------------------------------------------

export class CorpseBloomSkillEffect implements SkillEffect {
  readonly skillId = 'corpse_bloom';
  readonly type = 'active' as const;
  readonly activationMode = 'instant' as const;

  private readonly deps: CorpseBloomDeps;
  private readonly config: CorpseBloomConfig;

  private _cooldownRemaining = 0;
  private _activationsCount = 0;
  private _lastBurstDamage = 0;
  private _lastBurstEnemiesHit = 0;
  private _lastBloomChainApplied = 0;
  private _lastBloomChainExtended = 0;

  constructor(deps: CorpseBloomDeps, config: Partial<CorpseBloomConfig> = {}) {
    this.deps = deps;
    this.config = { ...DEFAULT_CONFIG, ...config };
  }

  // --- State getters (for assertions) ---

  get cooldown(): number { return this._cooldownRemaining; }
  get activationsCount(): number { return this._activationsCount; }
  get lastBurstDamage(): number { return this._lastBurstDamage; }
  get lastBurstEnemiesHit(): number { return this._lastBurstEnemiesHit; }
  get lastBloomChainApplied(): number { return this._lastBloomChainApplied; }
  get lastBloomChainExtended(): number { return this._lastBloomChainExtended; }

  // --- SkillEffect interface ---

  canActivate(): boolean {
    if (this._cooldownRemaining > 0) return false;

    // Must have at least one poisoned corpse within range.
    const playerPos = this.deps.targeting.getPlayerPosition();
    const corpses = this.deps.corpses.getPoisonedCorpsesInRange(
      playerPos,
      this.config.targetRangeTiles,
    );
    return corpses.length > 0;
  }

  activate(): boolean {
    if (!this.canActivate()) return false;

    const playerPos = this.deps.targeting.getPlayerPosition();
    const corpses = this.deps.corpses.getPoisonedCorpsesInRange(
      playerPos,
      this.config.targetRangeTiles,
    );
    if (corpses.length === 0) return false;

    // Select nearest poisoned corpse.
    const target = this.selectNearestCorpse(corpses, playerPos);

    // Compute burst damage from remaining poison on the corpse.
    const burstDamage = this.computeBurstDamage(target.corpseId);
    if (burstDamage <= 0) {
      // Edge case: poison data present but zero remaining — treat as invalid.
      return false;
    }

    // Remove poison from corpse (consumed by detonation).
    this.deps.corpses.removePoisonFromCorpse(target.corpseId);

    // Apply burst damage to all living enemies within radius of the corpse.
    const enemies = this.deps.targeting.getEnemiesInRange(
      target.position,
      this.config.burstRadiusTiles,
    );

    let enemiesHit = 0;
    for (const enemy of enemies) {
      this.deps.damage.applyBurst(enemy.entityId, burstDamage, 'corpse_bloom');
      enemiesHit++;
    }

    // Phase 2: Bloom Chain post-burst effects.
    const bloomChainResults = this.applyBloomChain(enemies, target.position);

    // Record state.
    this._lastBurstDamage = burstDamage;
    this._lastBurstEnemiesHit = enemiesHit;
    this._lastBloomChainApplied = bloomChainResults.applied;
    this._lastBloomChainExtended = bloomChainResults.extended;
    this._cooldownRemaining = this.config.cooldownMs;
    this._activationsCount++;

    return true;
  }

  deactivate(): void {
    // Instant activation — no toggle state.
  }

  isActive(): boolean {
    return false;
  }

  update(delta: number): void {
    if (this._cooldownRemaining > 0) {
      this._cooldownRemaining = Math.max(0, this._cooldownRemaining - delta);
    }
  }

  destroy(): void {
    this._cooldownRemaining = 0;
  }

  // ---------------------------------------------------------------------------
  // Private helpers
  // ---------------------------------------------------------------------------

  /**
   * Select the nearest poisoned corpse to the given center point.
   */
  private selectNearestCorpse(
    corpses: ReadonlyArray<CorpseRecord>,
    center: { x: number; y: number },
  ): CorpseRecord {
    let nearest = corpses[0];
    let nearestDist = Infinity;

    for (const corpse of corpses) {
      const dx = corpse.position.x - center.x;
      const dy = corpse.position.y - center.y;
      const dist = dx * dx + dy * dy; // Squared — no sqrt needed for comparison.
      if (dist < nearestDist) {
        nearestDist = dist;
        nearest = corpse;
      }
    }

    return nearest;
  }

  /**
   * Sum remaining DoT damage across all `poison` instances on a corpse.
   *
   * Formula per instance: `intensity × stacks × (remainingMs / tickIntervalMs)`.
   * This equals per-tick damage × remaining ticks — the full remaining DoT
   * budget delivered at once.
   */
  private computeBurstDamage(corpseId: string): number {
    const instances = this.deps.corpses.getPoisonInstances(corpseId);
    let total = 0;

    for (const inst of instances) {
      if (inst.remainingMs <= 0) continue;
      total += inst.intensity * inst.stacks * (inst.remainingMs / inst.tickIntervalMs);
    }

    return total;
  }

  /**
   * Bloom Chain upgrade: after burst, applies DoT to non-poisoned enemies
   * and extends existing DoT on already-poisoned enemies in the blast radius.
   */
  private applyBloomChain(
    enemies: ReadonlyArray<CorpseBloomTarget>,
    _corpsePos: { x: number; y: number },
  ): { applied: number; extended: number } {
    const burstDotDuration = this.deps.resolver.getAdditiveEffect(
      'corpse_bloom',
      'burstDotDurationMs',
    );
    const existingDotExtension = this.deps.resolver.getAdditiveEffect(
      'corpse_bloom',
      'existingDotExtensionMs',
    );

    // No Bloom Chain upgrade — nothing to do.
    if (burstDotDuration <= 0 && existingDotExtension <= 0) {
      return { applied: 0, extended: 0 };
    }

    let applied = 0;
    let extended = 0;

    for (const enemy of enemies) {
      if (this.deps.statusEffects.hasPoisonStatus(enemy.entityId)) {
        // Already poisoned: extend existing DoT.
        if (existingDotExtension > 0) {
          this.deps.statusEffects.extendPoison(
            enemy.entityId,
            existingDotExtension,
          );
          extended++;
        }
      } else {
        // Not poisoned: apply fresh DoT from the bloom.
        if (burstDotDuration > 0) {
          this.deps.statusEffects.applyPoison(
            enemy.entityId,
            burstDotDuration,
            3, // Base intensity — matches Taint baseline.
            'corpse_bloom',
          );
          applied++;
        }
      }
    }

    return { applied, extended };
  }
}
